import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, type PlanComparison } from '../lib/api';
import type { Board } from '../lib/types';
import { SettingsCard } from '@allrouted/ui/Field';
import { IconButton, RefreshIcon, TrashIcon } from '@allrouted/ui/Icons';
import { useUi } from '@allrouted/ui/Toaster';
import { RouteMap } from './RouteMap';
import { RoutesPanel } from './RoutesPanel';
import { Resizer, usePaneSize } from '@allrouted/ui/Resizer';
import { distance, shortDate } from '../lib/format';

/**
 * Simulations: rehearsing a day before committing to it.
 *
 * The operator describes them as somewhere to test an optimization before it
 * touches the live schedule, and as templates -- run the day under different
 * assumptions, then import the one you like into operations.
 *
 * This screen used to list them and nothing else, which made them impossible to
 * use for their stated purpose: you could create a simulation and promote it,
 * but never look at what you were about to promote. It now shows the chosen
 * simulation on the same map and timeline as the live day, and above them the
 * one comparison that decides the question -- is this better than what we have,
 * and what does moving to it cost.
 *
 * Editing works here too. A simulation is the safe place to drag a stop, which
 * is exactly why dragging has to be possible in it.
 */

interface Plan extends Record<string, unknown> {
  id: string;
  kind: string;
  status: string;
  name: string | null;
  date: string;
  route_count: number;
  stats: { assigned?: number; unassigned?: number } | null;
}

const hoursMinutes = (s: number) => `${Math.floor(s / 3600)}h${String(Math.round((s % 3600) / 60)).padStart(2, '0')}`;

export function SimulationsView({
  territoryId,
  date,
  onPromoted,
}: {
  territoryId: string | null;
  date: string;
  onPromoted: () => void;
}) {
  const ui = useUi();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [board, setBoard] = useState<Board | null>(null);
  const [comparison, setComparison] = useState<PlanComparison | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [name, setName] = useState('');
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const timeline = usePaneSize('allrouted.sim.timeline', 240, { min: 120, max: 560 });

  const simulations = useMemo(() => plans.filter((p) => p.kind === 'simulation'), [plans]);

  const load = useCallback(async () => {
    if (!territoryId) return;
    const { plans: list } = await api.plans(territoryId);
    setPlans(list as Plan[]);
  }, [territoryId]);

  useEffect(() => {
    void load();
  }, [load]);

  /* Whatever is selected, its board and its comparison, together. */
  const open = useCallback(
    async (planId: string) => {
      if (!territoryId) return;
      setSelectedId(planId);
      setBoard(null);
      setComparison(null);
      try {
        const [b, c] = await Promise.all([
          api.planBoard(territoryId, planId),
          api.comparePlan(territoryId, planId).catch(() => null),
        ]);
        setBoard(b);
        setComparison(c);
      } catch (err) {
        ui.notify('error', 'Could not open', err instanceof ApiError ? err.message : String(err));
      }
    },
    [territoryId, ui],
  );

  /* Reopen after any change, so the map and the numbers never lag the plan. */
  const reopen = useCallback(async () => {
    await load();
    if (selectedId) await open(selectedId);
  }, [load, open, selectedId]);

  const create = async () => {
    if (!territoryId) return;
    setRunning(true);
    try {
      const { planId } = await api.optimize(territoryId, date, {
        kind: 'simulation',
        name: name.trim() || undefined,
      });
      setName('');
      ui.notify('ok', 'Building', 'It appears here when the solver finishes.');
      /* Poll until it settles rather than making the operator press refresh. */
      for (let i = 0; i < 45; i += 1) {
        await new Promise((r) => setTimeout(r, 2000));
        const { plans: list } = await api.plans(territoryId);
        const made = (list as Plan[]).find((p) => p.id === planId);
        if (made && made.status !== 'optimizing') {
          setPlans(list as Plan[]);
          await open(planId);
          break;
        }
      }
    } catch (err) {
      ui.notify('error', 'Could not build', err instanceof ApiError ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  const promote = async (plan: Plan) => {
    if (!territoryId) return;
    const ok = await ui.confirm({
      title: `Import "${plan.name ?? shortDate(plan.date)}" into the live day?`,
      body: 'The operations plan for that date is replaced. Work already executed is kept where it happened, and locked routes are left alone.',
    });
    if (!ok) return;

    setBusy(plan.id);
    try {
      const result = await api.promotePlan(territoryId, plan.id);
      ui.notify(
        'ok',
        'Imported',
        `${result.routes} routes and ${result.stops} stops are now the live day.` +
          (result.skippedLocked.length ? ` ${result.skippedLocked.length} locked routes were left alone.` : ''),
      );
      onPromoted();
      await reopen();
    } catch (err) {
      /*
       * The refusal that matters: it will not erase work already done. Said
       * plainly rather than as a failure, because it is the safety feature
       * working.
       */
      ui.notify(
        'error',
        'Not imported',
        err instanceof ApiError ? err.message : String(err),
      );
    } finally {
      setBusy(null);
    }
  };

  const remove = async (plan: Plan) => {
    if (!territoryId) return;
    const ok = await ui.confirm({
      title: 'Delete this simulation?',
      body: 'It is only a rehearsal; the live day is untouched.',
    });
    if (!ok) return;
    try {
      await api.deletePlan(territoryId, plan.id);
      if (selectedId === plan.id) {
        setSelectedId(null);
        setBoard(null);
        setComparison(null);
      }
      await load();
    } catch (err) {
      ui.notify('error', 'Could not delete', err instanceof ApiError ? err.message : String(err));
    }
  };

  /** Dragging a stop inside a simulation, which is the safe place to try one. */
  const moveStop = useCallback(
    async (stopId: string, toRouteId: string, toSequence: number) => {
      if (!territoryId) return;
      await api.moveStop(territoryId, stopId, toRouteId, toSequence);
      if (selectedId) setBoard(await api.planBoard(territoryId, selectedId));
      if (selectedId) setComparison(await api.comparePlan(territoryId, selectedId).catch(() => null));
    },
    [territoryId, selectedId],
  );

  if (!territoryId) return <div className="empty">Select a territory.</div>;

  const selected = simulations.find((p) => p.id === selectedId) ?? null;

  return (
    <div className="sim-layout">
      <aside className="sim-list">
        <SettingsCard
          title="Simulations"
          description={`Rehearsals for ${date}. The live day is never touched until one is imported.`}
          editing={false}
          actions={
            <IconButton label="Refresh" onClick={() => void load()}>
              <RefreshIcon />
            </IconButton>
          }
        >
          <div className="sim-new">
            <input
              className="field"
              value={name}
              placeholder="What are you trying? e.g. Without Logan"
              onChange={(e) => setName(e.target.value)}
            />
            <button className="btn btn-primary" type="button" onClick={() => void create()} disabled={running}>
              {running ? 'Building' : 'Build one'}
            </button>
          </div>

          {simulations.length === 0 ? (
            <p className="hint">
              None yet. Build one to try a different plan for {date} without touching the live day.
            </p>
          ) : (
            <ul className="sim-items">
              {simulations.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="sim-item"
                    aria-current={p.id === selectedId ? 'true' : undefined}
                    onClick={() => void open(p.id)}
                  >
                    <strong>{p.name ?? `Simulation ${shortDate(p.date)}`}</strong>
                    <span className="dim">
                      {shortDate(p.date)} · {p.route_count} routes · {p.stats?.assigned ?? 0} stops
                      {p.stats?.unassigned ? ` · ${p.stats.unassigned} unassigned` : ''}
                    </span>
                  </button>
                  <div className="sim-item-actions">
                    <button
                      className="btn"
                      type="button"
                      onClick={() => void promote(p)}
                      disabled={busy === p.id}
                    >
                      {busy === p.id ? 'Importing' : 'Import'}
                    </button>
                    <IconButton label="Delete this simulation" tone="danger" onClick={() => void remove(p)}>
                      <TrashIcon />
                    </IconButton>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SettingsCard>
      </aside>

      <section className="sim-detail">
        {!selected ? (
          <div className="empty">Choose a simulation to see its routes.</div>
        ) : (
          <>
            {comparison ? (
              <Comparison c={comparison} unit={board?.territory.distanceUnit ?? 'mi'} />
            ) : null}

            <div className="sim-board" style={{ gridTemplateRows: `1fr auto ${timeline.size}px` }}>
              <div className="sim-map">
                <RouteMap
                  board={board}
                  selectedRouteId={selectedRouteId}
                  hiddenRouteIds={hidden}
                  onSelectRoute={setSelectedRouteId}
                />
              </div>
              {/* Dragging down shrinks the routes grid beneath it. */}
              <Resizer
                orientation="horizontal"
                direction={-1}
                onResize={timeline.onResize}
                label="Resize the routes grid"
              />
              <div className="sim-timeline">
                <RoutesPanel
                  board={board}
                  selectedRouteId={selectedRouteId}
                  hiddenRouteIds={hidden}
                  onSelectRoute={setSelectedRouteId}
                  onSelectStop={(routeId) => setSelectedRouteId(routeId)}
                  /*
                   * A rehearsal has no order drawer and no route drawer behind
                   * it; selecting the lane is all this column can usefully do.
                   */
                  onOpenRoute={(routeId) => setSelectedRouteId(routeId)}
                  onToggleHidden={(routeId) =>
                    setHidden((h) => {
                      const next = new Set(h);
                      if (next.has(routeId)) next.delete(routeId);
                      else next.add(routeId);
                      return next;
                    })
                  }
                  onToggleLock={async () => {
                    /* Locking protects a route from re-optimisation of the live
                     * day. Inside a rehearsal it would mean nothing. */
                    ui.notify('info', 'Not here', 'Lock routes on the live day, not in a rehearsal.');
                  }}
                  onMoveStop={moveStop}
                />
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

/**
 * The simulation against the day it would replace.
 *
 * Both columns and then the difference, because "twelve miles shorter" is only
 * half the answer -- the other half is how many orders change hands to get it,
 * which is what the dispatcher has to explain to a technician.
 */
function Comparison({ c, unit }: { c: PlanComparison; unit: 'mi' | 'km' }) {
  const rows: { label: string; ops: string; sim: string; better: number }[] = [
    {
      label: 'Routes',
      ops: String(c.operations.routes),
      sim: String(c.simulation.routes),
      better: c.operations.routes - c.simulation.routes,
    },
    {
      label: 'Unassigned',
      ops: String(c.operations.unassigned),
      sim: String(c.simulation.unassigned),
      better: c.operations.unassigned - c.simulation.unassigned,
    },
    {
      label: 'Distance',
      ops: distance(c.operations.distanceMeters, unit),
      sim: distance(c.simulation.distanceMeters, unit),
      better: c.operations.distanceMeters - c.simulation.distanceMeters,
    },
    {
      label: 'Driving',
      ops: hoursMinutes(c.operations.driveSeconds),
      sim: hoursMinutes(c.simulation.driveSeconds),
      better: c.operations.driveSeconds - c.simulation.driveSeconds,
    },
    {
      label: 'Windows at risk',
      ops: String(c.operations.windowsAtRisk),
      sim: String(c.simulation.windowsAtRisk),
      better: c.operations.windowsAtRisk - c.simulation.windowsAtRisk,
    },
  ];

  return (
    <div className="sim-compare">
      <table className="grid">
        <thead>
          <tr>
            <th />
            <th className="num">Live day</th>
            <th className="num">This simulation</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td>{r.label}</td>
              <td className="num dim">{r.ops}</td>
              <td className="num">
                <strong>{r.sim}</strong>
              </td>
              <td>
                {/* Green only when the simulation is genuinely better. */}
                {r.better > 0 ? (
                  <span className="pill" data-tone="ok">
                    better
                  </span>
                ) : r.better < 0 ? (
                  <span className="pill" data-tone="warn">
                    worse
                  </span>
                ) : (
                  <span className="dim">same</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="hint">
        Importing this would move <strong>{c.movedCount}</strong>{' '}
        {c.movedCount === 1 ? 'order' : 'orders'} to a different technician
        {c.resequencedCount > 0 ? `, resequence ${c.resequencedCount} more` : ''}
        {c.droppedCount > 0 ? `, and leave ${c.droppedCount} unplanned` : ''}.
      </p>
    </div>
  );
}
